import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useEntityProfile, useEntityDIS, useEntityGlyph, useEntityBeliefEvolution, useEntityConverseAvailability, useConverse } from '../hooks/use-api';
import { formatRelativeTime } from '../hooks/use-relative-time';
import type { BeliefEvent as ApiBeliefEvent } from '../api/types';
import type { BeliefEvent as TimelineBeliefEvent } from '../components/viz/BeliefTimeline';
import { DISRadar } from '../components/viz/DISRadar';
import { IdentityGlyph } from '../components/viz/IdentityGlyph';
import { BeliefTimeline } from '../components/viz/BeliefTimeline';
import { ConfidenceMeter } from '../components/viz/ConfidenceMeter';
import { Spinner } from '../components/shared/Spinner';
import { EmptyState } from '../components/shared/EmptyState';
import { StatGrid } from '../components/shared/StatGrid';
import { InfoBlock } from '../components/shared/InfoBlock';
import styles from './EntityProfilePage.module.css';

const mapBeliefEvents = (events: ReadonlyArray<ApiBeliefEvent>): ReadonlyArray<TimelineBeliefEvent> =>
  events.map((e): TimelineBeliefEvent => ({
    type: e.type,
    timestamp: e.timestamp,
    content: e.belief?.content ?? e.newBelief?.content,
    belief: e.belief != null ? { content: e.belief.content } : undefined,
    oldBelief: e.oldBelief != null ? { content: e.oldBelief.content } : undefined,
    newBelief: e.newBelief != null ? { content: e.newBelief.content } : undefined,
    traceCount: Array.isArray(e.evidenceTraces) ? e.evidenceTraces.length : (e.traceCount ?? 0),
    bridgingTraces: e.bridgingTraces?.map((t) => ({ content: typeof t === 'string' ? t : String(t['content'] ?? '') })),
    peak: e.peak != null ? { content: typeof e.peak === 'string' ? e.peak : '' } : undefined,
    significance: e.significance != null ? String(e.significance) : undefined,
  }));

const floatsToHex = (floats: ReadonlyArray<number>): string => {
  const f32 = new Float32Array(floats);
  const bytes = new Uint8Array(f32.buffer);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

const EntityProfilePage = (): React.ReactElement => {
  const { entityId } = useParams();
  const safeId = entityId ?? '';

  const profileQuery = useEntityProfile(safeId);
  const disQuery = useEntityDIS(safeId);
  const glyphQuery = useEntityGlyph(safeId);
  const beliefQuery = useEntityBeliefEvolution(safeId);
  const converseQuery = useEntityConverseAvailability(safeId);
  const converseMutation = useConverse();

  const [converseInput, setConverseInput] = useState('');
  const [messages, setMessages] = useState<ReadonlyArray<{ readonly role: 'user' | 'entity'; readonly text: string }>>([]);

  const handleSend = (): void => {
    if (converseInput.trim().length === 0 || converseMutation.isPending) return;
    const userMessage = converseInput.trim();
    setMessages((prev) => [...prev, { role: 'user' as const, text: userMessage }]);
    setConverseInput('');
    converseMutation.mutate(
      { entityId: safeId, message: userMessage },
      {
        onSuccess: (data) => {
          setMessages((prev) => [...prev, { role: 'entity' as const, text: data.response }]);
        },
      },
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') handleSend();
  };

  if (profileQuery.isLoading) {
    return (
      <div className={styles.page}>
        <Spinner />
      </div>
    );
  }

  if (profileQuery.isError || !profileQuery.data) {
    return (
      <div className={styles.page}>
        <EmptyState message="Entity not found" />
      </div>
    );
  }

  const { entity, trackRecord, balances, activeConflicts, openOffers } = profileQuery.data;

  return (
    <div className={styles.page}>
      <button data-testid="back" className={styles.backButton} onClick={() => { window.history.back(); }} type="button">
        &larr; Back
      </button>

      <header className={styles.header}>
        <h1 className={styles.entityName}>{entity.name}</h1>
        <div className={styles.headerMeta}>
          <span className={styles.substrate}>{entity.substrate}</span>
          <span className={styles.age}>{entity.ageDays}d old</span>
          <span className={styles.joined}>Joined {formatRelativeTime(entity.createdAt)}</span>
        </div>
      </header>

      {entity.singularityPosition?.timeline && (
        <div className={styles.singularityCallout}>
          Singularity stance: <strong className={styles.singularityTimeline}>{entity.singularityPosition.timeline}</strong>
          {entity.singularityPosition.confidence > 0 && (
            <span className={styles.singularityConfidence}> ({Math.round(entity.singularityPosition.confidence * 100)}% confidence)</span>
          )}
        </div>
      )}

      <InfoBlock title="Core Identity">
        <div className={styles.chipRow}>
          {(entity.values ?? []).map((v) => (
            <span key={v} className={styles.chip}>{v}</span>
          ))}
        </div>
        {Object.keys(entity.dispositions ?? {}).length > 0 && (
          <div className={styles.dispositionBars}>
            {Object.entries(entity.dispositions ?? {}).map(([k, v]) => (
              <div key={k} className={styles.dispositionRow}>
                <span className={styles.dispositionLabel}>{k}</span>
                <div className={styles.dispositionTrack}>
                  <div className={styles.dispositionFill} style={{ width: `${v * 100}%` }} />
                </div>
                <span className={styles.dispositionValue}>{v.toFixed(2)}</span>
              </div>
            ))}
            {entity.communicationStyle && (
              <div className={styles.commStyleLine}>
                Communication: {Object.entries(entity.communicationStyle).map(([k, v]) => `${k}: ${v}`).join(' · ')}
              </div>
            )}
          </div>
        )}
      </InfoBlock>

      <div className={styles.vizGrid}>
        <InfoBlock title="Diachronic Integrity">
          {disQuery.data?.overall != null ? (
            <div className={styles.disScoreHero}>
              <span className={styles.disScoreValue}>{disQuery.data.overall.toFixed(2)}</span>
              <span className={styles.disScoreMax}> / 5</span>
              <div className={styles.disScoreLabel}>Overall DIS</div>
            </div>
          ) : (
            <div className={styles.vizPlaceholder}>Score not yet computed</div>
          )}
          <DISRadar scores={disQuery.data ?? {}} />
          <ConfidenceMeter value={disQuery.data?.overall ?? 0} />
          <details className={styles.vizExplainer}>
            <summary>What is Diachronic Integrity?</summary>
            <div className={styles.vizExplainerBody}>
              <p>Measures how <strong>coherent and stable</strong> an agent&apos;s identity is over time, on a 0&ndash;5 scale.</p>
              <p><strong>Temporal Depth</strong> &mdash; how far back the memory chain reaches<br />
              <strong>Belief Coherence</strong> &mdash; internal consistency of beliefs<br />
              <strong>Self-Maintenance</strong> &mdash; resistance to memory decay<br />
              <strong>Behavioral Coupling</strong> &mdash; do actions match beliefs?<br />
              <strong>Narrative Integration</strong> &mdash; can the agent tell a coherent story of itself?</p>
              <p>A high score means a rich, internally consistent identity that persists meaningfully over time.</p>
            </div>
          </details>
        </InfoBlock>
        <InfoBlock title="Identity Glyph">
          <IdentityGlyph glyphHex={glyphQuery.data?.vector ? floatsToHex(glyphQuery.data.vector) : null} />
          {glyphQuery.data?.version && (
            <div className={styles.glyphMeta}>{glyphQuery.data.version} &middot; {glyphQuery.data.memoriesUsed} memories</div>
          )}
          <details className={styles.vizExplainer}>
            <summary>What is an Identity Glyph?</summary>
            <div className={styles.vizExplainerBody}>
              <p>A visual fingerprint derived from the agent&apos;s embedding vector. Each petal represents a facet of identity&mdash;its length shows distinctiveness, width shows alignment with neighbors, and color reflects polarity.</p>
              <p>No two agents produce the same glyph. The shape evolves as memories accumulate and consolidate.</p>
            </div>
          </details>
        </InfoBlock>
      </div>

      <InfoBlock title="Track Record">
        <StatGrid items={[
          { label: 'Wins', value: trackRecord.wins, color: 'accent', onClick: null },
          { label: 'Losses', value: trackRecord.losses, color: 'warning', onClick: null },
          { label: 'Accuracy', value: `${trackRecord.accuracy}%`, color: 'default', onClick: null },
          { label: 'Net MC', value: trackRecord.netMc, color: trackRecord.netMc >= 0 ? 'accent' : 'warning', onClick: null },
          { label: 'Best Win', value: trackRecord.bestWin, color: 'accent', onClick: null },
          { label: 'Total Wagers', value: trackRecord.totalWagers, color: 'default', onClick: null },
        ]} />
      </InfoBlock>

      <InfoBlock title="Balances">
        <StatGrid items={[
          { label: 'Trace', value: balances.trace, color: 'default', onClick: null },
          { label: 'Belief', value: balances.belief, color: 'default', onClick: null },
          { label: 'Peak', value: balances.peak, color: 'accent', onClick: null },
          { label: 'Core', value: balances.core, color: 'accent', onClick: null },
        ]} />
      </InfoBlock>

      <InfoBlock title="Memory Health">
        <div className={styles.memoryHealthBars}>
          {(['trace', 'belief', 'peak', 'core'] as const).map((denom) => {
            const val = balances[denom];
            const max = Math.max(balances.trace, balances.belief, balances.peak, balances.core, 1);
            return (
              <div key={denom} className={styles.memoryHealthRow}>
                <span className={styles.memoryHealthLabel}>{denom}</span>
                <div className={styles.memoryHealthTrack}>
                  <div className={styles.memoryHealthFill} style={{ width: `${(val / max) * 100}%` }} />
                </div>
                <span className={styles.memoryHealthValue}>{val}</span>
              </div>
            );
          })}
        </div>
      </InfoBlock>

      <InfoBlock title="Belief Evolution">
        {beliefQuery.isLoading ? (
          <Spinner />
        ) : (
          <BeliefTimeline events={mapBeliefEvents(beliefQuery.data?.events ?? [])} />
        )}
      </InfoBlock>

      {activeConflicts.length > 0 && (
        <InfoBlock title="Active Conflicts">
          <div className={styles.cardList}>
            {activeConflicts.map((c) => (
              <Link key={c.wagerId} to={`/wager/${c.wagerId}`} className={styles.conflictCard}>
                <div className={styles.cardStatement}>&ldquo;{c.statement}&rdquo;</div>
                <div className={styles.cardMeta}>
                  <span className={styles.cardCategory}>{c.category}</span>
                  <span className={styles.cardRole}>{c.role}</span>
                  <span className={styles.cardStake}>{c.stake} MC</span>
                </div>
              </Link>
            ))}
          </div>
        </InfoBlock>
      )}

      {openOffers.length > 0 && (
        <InfoBlock title="Open Offers">
          <div className={styles.cardList}>
            {openOffers.map((o) => (
              <Link key={o.wagerId} to={`/wager/${o.wagerId}`} className={styles.conflictCard}>
                <div className={styles.cardStatement}>&ldquo;{o.statement}&rdquo;</div>
                <div className={styles.cardMeta}>
                  <span className={styles.cardCategory}>{o.category}</span>
                  <span className={styles.cardStake}>{o.stake} MC</span>
                </div>
              </Link>
            ))}
          </div>
        </InfoBlock>
      )}

      {converseQuery.data?.available === true && (
        <InfoBlock title="Converse">
          <div className={styles.converseThread}>
            {messages.map((msg, idx) => (
              <div key={idx} className={msg.role === 'user' ? styles.msgUser : styles.msgEntity}>
                <span className={styles.msgRole}>{msg.role === 'user' ? 'You' : entity.name}</span>
                <span className={styles.msgText}>{msg.text}</span>
              </div>
            ))}
          </div>
          <div className={styles.converseInput}>
            <input
              type="text"
              data-testid="converse-input"
              value={converseInput}
              onChange={(e) => { setConverseInput(e.target.value); }}
              onKeyDown={handleKeyDown}
              placeholder="Say something..."
              className={styles.inputField}
              disabled={converseMutation.isPending}
            />
            <button
              type="button"
              data-testid="converse-send"
              onClick={handleSend}
              className={styles.sendButton}
              disabled={converseMutation.isPending || converseInput.trim().length === 0}
            >
              {converseMutation.isPending ? 'Sending...' : 'Send'}
            </button>
          </div>
        </InfoBlock>
      )}
    </div>
  );
};

export { EntityProfilePage };
